import { customRef, nextTick, watch } from 'vue-demi'
import { useRoute, useRouter } from '@nuxtjs/composition-api'
import { toValue, tryOnScopeDispose } from '@vueuse/shared'
import type { Ref } from '@nuxtjs/composition-api'
import type { MaybeRefOrGetter } from '@vueuse/shared'
import type { RawLocation as LocationAsRelativeRaw, default as VueRouter } from 'vue-router'

import type { ReactiveRouteOptionsWithTransform, RouteParamValueRaw } from './_types'

const _queue = new WeakMap<VueRouter, Map<string, any>>()

export function useRouteParams(
  name: string
): Ref<null | string | string[]>

export function useRouteParams<
  T extends RouteParamValueRaw = RouteParamValueRaw,
  K = T,
>(
  name: string,
  defaultValue?: MaybeRefOrGetter<T>,
  options?: ReactiveRouteOptionsWithTransform<T, K>
): Ref<K>

export function useRouteParams<
  T extends RouteParamValueRaw = RouteParamValueRaw,
  K = T,
>(
  name: string,
  defaultValue?: MaybeRefOrGetter<T>,
  options: ReactiveRouteOptionsWithTransform<T, K> = {},
): Ref<K> {
  const {
    mode = 'replace',
    route = useRoute(),
    router = useRouter(),
    transform = value => value as any as K,
  } = options

  if (!_queue.has(router))
    _queue.set(router, new Map())

  const _paramsQueue = _queue.get(router)!

  let param = route.value.params[name] as any

  tryOnScopeDispose(() => {
    param = undefined
  })

  let _trigger: () => void

  const proxy = customRef<any>((track, trigger) => {
    _trigger = trigger

    return {
      get() {
        track()

        return transform(param !== undefined ? param : toValue(defaultValue))
      },
      set(v) {
        if (param === v)
          return

        param = v
        _paramsQueue.set(name, v)

        trigger()

        nextTick(() => {
          if (_paramsQueue.size === 0)
            return

          const newParams = Object.fromEntries(_paramsQueue.entries())
          _paramsQueue.clear()

          const { params, query, hash } = route.value

          router[toValue(mode)]({
            params: {
              ...params,
              ...newParams,
            },
            query,
            hash,
          } as LocationAsRelativeRaw)
        })
      },
    }
  })

  watch(
    () => route.value.params[name],
    (v) => {
      param = v

      _trigger()
    },
    { flush: 'sync' },
  )

  return proxy as Ref<K>
}